import Ember from 'ember';
import { dropTask, restartableTask } from 'ember-concurrency';
import moment from 'moment-timezone';
// @ts-ignore
import AlertOptionComponent from 'volta/components/filters/alert-option';
import InputFilter from 'volta/components/filters/input-filter';
import SelectFilter from 'volta/components/filters/select-filter';
import SkusPartTypeFilter from 'volta/components/filters/skus-part-type-filter';
import SkusSupplierFilter from 'volta/components/filters/skus-supplier-filter';
import SkusWarehouseFilter from 'volta/components/filters/skus-warehouse-filter';
import SkusWorkshopFilter from 'volta/components/filters/skus-workshop-filter';
import TagsFilter from 'volta/components/filters/tags-filter';
import TokenFilter from 'volta/components/filters/token-filter';
import { AcquisitionCodesOptions } from 'volta/models/constants/acquisition-codes';
import { BufferZonesOptions } from 'volta/models/constants/buffer-zones';
import { PlanningMethodologiesManagedOptions } from 'volta/models/constants/planning-methodologies';
import JsonApiInfinityModel from 'volta/models/jsonapi-infinity-model';
import PlanningModel, { PlanningEvents, PlanningResourceName } from 'volta/models/planning';
import { SyncAlertOptions } from 'volta/models/planning-order';
import Simulation from 'volta/models/simulation';
import { SkuResourceName } from 'volta/models/sku';
import { SkuTaskResourceName, TaskListChangeEvents } from 'volta/models/task';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler, TError } from 'volta/utils/error-utils';
import {
  beforeQueryWorkshop,
  clearFilter,
  inFilter,
  stringListToArray
} from 'volta/utils/filters-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type RouterService from '@ember/routing/router-service';
import type Infinity from 'ember-infinity/services/infinity';
import type IntlService from 'ember-intl/services/intl';
import type BvFlashService from 'volta/services/bv-flash';
import type BvMetricsService from 'volta/services/bv-metrics';
import type CustomFieldDefinitionsService from 'volta/services/custom-field-definitions-service';
import type EventStreamService from 'volta/services/event-stream';
import type PlanningOrderApi from 'volta/services/planning-order-api';
import type StoreService from 'volta/services/store';
import type AppSettings from 'volta/services/app-settings';
import type { UTask } from 'volta/models/types/tasks';

const SORT_OPTIONS: TSortOption[] = [
  { key: 'priority', label: 'priority' },
  { key: 'sku.warehouseCode', label: 'warehouse' },
  { key: 'sku.skuDescription', label: 'part' },
  { key: 'sku.supplierCode', label: 'supplier' },
  { key: 'sku.task', label: 'tasks' },
  { key: 'reorderQty', label: 'reorderQty' },
  { key: 'sku.unitMeasure', label: 'unit' },
  { key: 'netFlow', label: 'netFlow' },
  { key: 'sku.voltaLeadTime', label: 'leadTimes' },
  { key: 'adu', label: 'adu' },
  { key: 'demandQty', label: 'demand' },
  { key: 'demandSpikeQty', label: 'spike' },
  { key: 'onHand', label: 'onHand' },
  { key: 'openSupplyQty', label: 'openSupply' },
  { key: 'syncAlert', label: 'syncAlert' },
  { key: 'coverage', label: 'coverage' }
];

export const PlanningFilterDefinitions = (
  store: StoreService,
  cfdService: CustomFieldDefinitionsService,
  warehouses?: string,
  showCoverage: boolean = false
) => {
  const warehouseIds = warehouses ? stringListToArray(clearFilter(warehouses)) : [];

  const normalDefs: Array<TFilterDefinition | undefined> = [
    {
      queryParam: 'warehouse',
      apiParam: 'warehouseId',
      comparators: ['in', 'ni'],
      component: SkusWarehouseFilter,
      componentArgs: {}
    },
    {
      queryParam: 'partType',
      apiParam: 'partTypeId',
      comparators: ['in', 'ni'],
      component: SkusPartTypeFilter,
      componentArgs: {}
    },
    {
      queryParam: 'deliveryWorkshop',
      apiParam: 'deliveryWorkshopId',
      comparators: ['in', 'ni'],
      component: SkusWorkshopFilter,
      componentArgs: { beforeQuery: beforeQueryWorkshop('DELIVERY', warehouses) }
    },
    {
      queryParam: 'workshop',
      apiParam: 'workshopId',
      comparators: ['in', 'ni'],
      component: SkusWorkshopFilter,
      componentArgs: { beforeQuery: beforeQueryWorkshop('WORKSHOP', warehouses) }
    },
    {
      queryParam: 'supplier',
      apiParam: 'supplierId',
      comparators: ['in', 'ni'],
      component: SkusSupplierFilter,
      componentArgs: {}
    },
    {
      queryParam: 'q',
      apiParam: 'q',
      comparators: ['lk'],
      component: InputFilter,
      hidden: true,
      componentArgs: { inputType: 'text' }
    },
    {
      queryParam: 'bufferZone',
      apiParam: 'bufferZone',
      comparators: ['in'],
      component: SelectFilter,
      componentArgs: { possibleValues: BufferZonesOptions }
    },
    {
      queryParam: 'acqCode',
      apiParam: 'acqCode',
      comparators: ['in'],
      component: SelectFilter,
      componentArgs: { possibleValues: AcquisitionCodesOptions }
    },
    {
      queryParam: 'methodology',
      apiParam: 'methodology',
      comparators: ['in'],
      component: SelectFilter,
      componentArgs: { possibleValues: PlanningMethodologiesManagedOptions }
    },
    {
      queryParam: 'tags',
      apiParam: 'partTags',
      comparators: ['any', 'all', 'ni'],
      component: TagsFilter,
      componentArgs: {
        warehouseIds,
        entityTypes: ['parts', 'skus']
      }
    },
    {
      queryParam: 'reorderQty',
      apiParam: 'reorderQty',
      comparators: ['lt', 'eq', 'gt'],
      component: InputFilter,
      componentArgs: { inputType: 'number' }
    },
    showCoverage
      ? {
          queryParam: 'coverage',
          apiParam: 'coverage',
          comparators: ['lt', 'gt', 'bt'],
          component: InputFilter,
          componentArgs: { inputType: 'number' }
        }
      : undefined,
    {
      queryParam: 'planner',
      apiParam: 'planner',
      comparators: ['in', 'ni'],
      component: TokenFilter,
      componentArgs: {
        warehouseIds,
        searchFn: (query: object) => {
          return store.customQuery('sku', 'planners', undefined, query);
        }
      }
    },
    {
      queryParam: 'buyer',
      apiParam: 'buyer',
      comparators: ['in', 'ni'],
      component: TokenFilter,
      componentArgs: {
        warehouseIds,
        searchFn: (query: object) => {
          return store.customQuery('sku', 'buyers', undefined, query);
        }
      }
    },
    {
      queryParam: 'syncAlert',
      apiParam: 'syncAlert',
      comparators: ['in'],
      component: SelectFilter,
      componentArgs: {
        possibleValues: SyncAlertOptions.filter((option) => option.key !== 'NONE'),
        itemComponent: AlertOptionComponent,
        valueProp: 'value'
      }
    }
  ];

  return (normalDefs.filter(Boolean) as TFilterDefinition[]).concat(
    cfdService.skusCustomFieldsFilters()
  );
};

interface IArgs {
  displayedSkuId?: string;
  queryParamsValues: TQueryParams;
  onRefreshManyPlannings?: (options?: { queryParams: TQueryParams }) => void;
  onSelectionChange?: (items: PlanningModel[]) => void;
  onFiltersChange?: (filters: Record<string, string>) => void;
  onItemTransition?: (planning: PlanningModel, options?: { queryParams: TQueryParams }) => void;
}

export default class PlanningList extends Component<IArgs> {
  // Services
  // ~~~~~~~

  @service appSettings!: AppSettings;
  @service bvFlash!: BvFlashService;
  @service bvMetrics!: BvMetricsService;
  @service customFieldDefinitionsService!: CustomFieldDefinitionsService;
  @service eventStream!: EventStreamService;
  @service infinity!: Infinity;
  @service intl!: IntlService;
  @service planningOrderApi!: PlanningOrderApi;
  @service router!: RouterService;
  @service store!: StoreService;

  // Properties
  // ~~~~~~~

  @tracked selectedItems: PlanningModel[] = [];
  @tracked showNewSimulationModal: boolean = false;
  @tracked showLoading: boolean = false;
  @tracked allSelected: boolean = false;
  @tracked selectedPOUserId?: string;
  @tracked sortOptions: TSortOption[] = SORT_OPTIONS.filter((option) => {
    return this.showCoverage || option.key !== 'coverage';
  });

  resourceName = PlanningResourceName;

  // Computed Properties
  // ~~~~~~~

  get selectedSkuIds() {
    return this.selectedItems.map((pl) => pl.skuInfo.skuId);
  }

  get showCoverage() {
    return this.appSettings.getFeature('planning.showCoverage') as boolean;
  }

  @cached
  get filterDefinitions() {
    return PlanningFilterDefinitions(
      this.store,
      this.customFieldDefinitionsService,
      this.args.queryParamsValues?.['warehouse'],
      this.showCoverage
    );
  }

  // Lifecyle Hooks
  // ~~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    const es = this.eventStream;
    const plCalcDoneEvents = [
      PlanningEvents.PlanningsCalculationDone,
      PlanningEvents.PlanningsGlobalCalculationDone
    ];
    es.addHandler(
      PlanningResourceName.plural,
      plCalcDoneEvents,
      this._onPlanningsCalculationDone,
      this
    );
    es.addHandler(SkuResourceName.plural, TaskListChangeEvents, this.handleTaskEvent, this);
  }

  willDestroy() {
    this.eventStream.removeHandlers(this);
    super.willDestroy();
  }

  // Actions
  // ~~~~~

  @action
  handleSelectionChange(items: PlanningModel[]) {
    this.bvMetrics.trackAction(this, 'handleSelectionChange', items.length.toString());
    this.selectedItems = items;
    this.args.onSelectionChange?.(this.selectedItems);
  }

  @action
  generatePlannings() {
    this.bvMetrics.trackAction(this, 'generatePlannings');
    this.showLoading = true;
    return this.generateManyCmd(this.selectedSkuIds).then(() => this._onGenerateManySuccess());
  }

  @action
  selectPlannings(selected: PlanningModel[]) {
    this.bvMetrics.trackAction(this, 'selectPlannings', undefined, selected.length);
    this.selectedItems = selected;
  }

  @action
  closeNewSimulationModal() {
    this.bvMetrics.trackAction(this, 'closeNewSimulationModal');
    this.showNewSimulationModal = false;
  }

  @action
  generateManyCmd(skuIds: string[]) {
    const today = moment.utc().format('YYYY-MM-DD');
    const payload = { date: today, skuIds, warehouseIds: [] };
    return PlanningModel.generateMany(payload).catch((reason) => this._onDefaultError(reason));
  }

  @action
  reloadPlannings(queryParams: TQueryParams) {
    this.args.onRefreshManyPlannings?.({ queryParams });
  }

  @action
  onCreateSimulationSuccess(simulation: Simulation) {
    this.showNewSimulationModal = false;
    this.router.transitionTo('protected.simulation', simulation.id);
  }

  // Tasks
  // ~~~~~~

  queryPlanningsTask = restartableTask(async (apiQuery = {}) => {
    try {
      const response: Ember.Array<PlanningModel> = await this.infinity.model(
        this.resourceName.singular,
        apiQuery,
        JsonApiInfinityModel
      );
      this.args.onFiltersChange?.(apiQuery.filter ?? {});
      return response;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });

  createDraftOrderTask = dropTask(async () => {
    this.bvMetrics.trackAction(this, 'createDraftOrder');
    const selectedPlanningIds = this.selectedItems.map((pl) => pl.id);
    try {
      const pos = await this.planningOrderApi.draftManyTask.perform(selectedPlanningIds);
      this.bvFlash.success(this.intl.t('planningsPage.createDraftOrderSuccess'));
      this.clearSelection();
      return pos;
    } catch (error) {
      this.bvFlash.error(error);
      return;
    }
  });

  // Helpers
  // ~~~~~~~

  overrideFilters(filters?: Array<{ key: string; value: string }>) {
    const filtersToApply: TFilterValues = {};

    (filters ?? []).forEach((filter: { key: string; value: string }) => {
      if (filter.value) {
        filtersToApply[filter.key] = {
          comparator: 'in',
          enabled: true,
          values: [filter.value]
        };
      }
    });

    return filtersToApply;
  }

  async handleTaskEvent(payload: any) {
    const task = normalize(SkuTaskResourceName.singular, payload) as UTask;
    if (task.taskType === 'PLANNING_TASK') {
      await this.store.query(this.resourceName.singular, {
        filter: {
          skuId: inFilter([task.linkedEntityId])
        }
      });
    }
  }

  private refreshPlannings(skuIds: string[] = []) {
    if (skuIds.length > 20 || !skuIds.length) {
      return this.args.onRefreshManyPlannings?.();
    }

    const query = {
      filter: {
        skuId: inFilter(skuIds)
      }
    };
    return this.store
      .query(PlanningResourceName.singular, query)
      .catch((error) => this._onDefaultError(error));
  }

  private clearSelection() {
    this.selectedItems = [];
  }

  // Private Callbacks
  // ~~~~~~~

  private _onPlanningsCalculationDone(payload: { skuIds: string[] }) {
    return this.refreshPlannings(payload.skuIds);
  }

  private _onGenerateManySuccess() {
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    this.clearSelection();
    this.showLoading = false;
  }

  private _onDefaultError(error: TError) {
    defaultErrorHandler(error);
    this.bvFlash.apiError(error);
  }
}
